import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  createChart, IChartApi, ISeriesApi,
  ColorType, CrosshairMode, PriceScaleMode,
  LineStyle, UTCTimestamp
} from 'lightweight-charts';
import { Candle, LiquidityZone, SMCResult, SMCOverlaySettings } from '../../types';
import { calcVWAP, calcAnchoredVWAP, findNearestCandle } from '../../utils/calc';

interface Props {
  candles: Candle[];
  zones: LiquidityZone[];
  anchorPoints: number[];
  onAddAnchor: (ts: number) => void;
  onRemoveAnchor: (ts: number) => void;
}

// ── Premium dark trading palette ─────────────────────────────────────────────
const C = {
  // Canvas
  bg:          '#131722',            // TradingView-style dark blue-black
  plotBg:      '#131722',
  // Axes + text
  text:        '#9fa5b0',            // crisp muted gray — legible without weight
  axisLine:    '#2e3548',            // defined but subdued axis border
  // Grid — barely there, structural only
  gridVert:    'rgba(42,46,57,0.22)', // ambient only — barely there
  gridHorz:    'rgba(42,46,57,0.22)',
  // Candles — strong, decisive
  bullBody:    '#2ec4a9',            // brighter teal — pops against #131722
  bullBorder:  '#2ec4a9',
  bullWick:    '#1a9e8a',            // slightly darker wick for depth
  bearBody:    '#f0534e',            // slightly brighter red
  bearBorder:  '#f0534e',
  bearWick:    '#c0403c',            // slightly darker wick
  // Volume — direction-matched, clearly secondary
  volUp:       'rgba(46,196,169,0.42)',
  volDn:       'rgba(240,83,78,0.40)',
  // Overlays
  vwap:        '#c9a84c',            // MER gold
  anchors:     ['#22d3ee','#a78bfa','#f472b6','#34d399'],
  // Crosshair
  crossLine:   'rgba(120,130,145,0.35)',
  crossLabel:  '#2a2e39',
};

const CHART_H = 300;   // dominant canvas — compact but authoritative

// ── Smart tick formatter ──────────────────────────────────────────────────────
function makeTickFormatter(candles: Candle[]) {
  const isDaily = candles.length >= 2 &&
    (candles[1].ts - candles[0].ts) >= 18 * 3600 * 1000;

  if (isDaily) {
    const MON = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return (time: number) => {
      const d   = new Date(time * 1000);
      const day = d.getUTCDate();
      const mon = MON[d.getUTCMonth()];
      // Show year on Jan 1, month on 1st of each month, otherwise day
      if (day === 1 && d.getUTCMonth() === 0) return String(d.getUTCFullYear());
      if (day === 1) return mon;
      return `${mon} ${day}`;
    };
  }
  // Intraday
  return (time: number) => {
    const d = new Date(time * 1000);
    return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
  };
}

export function CandleChart({ candles, zones, anchorPoints, onAddAnchor, onRemoveAnchor }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const chartRef     = useRef<IChartApi | null>(null);
  const candleRef    = useRef<ISeriesApi<'Candlestick'> | null>(null);
  const volRef       = useRef<ISeriesApi<'Histogram'> | null>(null);
  const vwapRef      = useRef<ISeriesApi<'Line'> | null>(null);
  const anchorRefs   = useRef<ISeriesApi<'Line'>[]>([]);
  const plinesRef    = useRef<any[]>([]);
  const [anchorMode, setAnchorMode] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    const chart = createChart(containerRef.current, {
      width:  containerRef.current.clientWidth,
      height: CHART_H,
      layout: {
        background: { type: ColorType.Solid, color: C.bg },
        textColor:  C.text,
        fontFamily: "'JetBrains Mono', 'Courier New', monospace",
        fontSize:   11,
      },
      grid: {
        vertLines: { color: C.gridVert, style: LineStyle.Solid },
        horzLines: { color: C.gridHorz, style: LineStyle.Solid },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color:                C.crossLine,
          width:                1,
          style:                LineStyle.Solid,
          labelBackgroundColor: C.crossLabel,
        },
        horzLine: {
          color:                C.crossLine,
          width:                1,
          style:                LineStyle.Solid,
          labelBackgroundColor: C.crossLabel,
        },
      },
      rightPriceScale: {
        borderColor:  C.axisLine,
        textColor:    C.text,
        mode:         PriceScaleMode.Normal,
        scaleMargins: { top: 0.04, bottom: 0.16 },
      },
      timeScale: {
        borderColor:       C.axisLine,
        timeVisible:       true,
        secondsVisible:    false,
        rightOffset:       5,
        barSpacing:        9,
        tickMarkFormatter: makeTickFormatter(candles),
      },
      handleScroll: true,
      handleScale:  true,
    });

    // Candlesticks — solid bodies, clear wicks
    const cs = (chart as any).addCandlestickSeries({
      upColor:         C.bullBody,
      downColor:       C.bearBody,
      borderUpColor:   C.bullBorder,
      borderDownColor: C.bearBorder,
      wickUpColor:     C.bullWick,
      wickDownColor:   C.bearWick,
      priceScaleId:    'right',
    });

    // Volume histogram — subordinate lower band
    const vs = (chart as any).addHistogramSeries({
      priceFormat:  { type: 'volume' },
      priceScaleId: 'vol',
    });
    chart.priceScale('vol').applyOptions({ scaleMargins: { top: 0.83, bottom: 0 } });

    // VWAP
    const ws = (chart as any).addLineSeries({
      color:                  C.vwap,
      lineWidth:              1,
      lineStyle:              LineStyle.Dashed,
      priceScaleId:           'right',
      crosshairMarkerVisible: false,
      lastValueVisible:       true,
      title:                  'VWAP',
    });

    chartRef.current  = chart;
    candleRef.current = cs;
    volRef.current    = vs;
    vwapRef.current   = ws;

    const ro = new ResizeObserver(e => {
      if (e[0] && chartRef.current) chartRef.current.applyOptions({ width: e[0].contentRect.width });
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      try { chart.remove(); } catch {}
      chartRef.current = candleRef.current = volRef.current = vwapRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-apply tick formatter when interval changes
  const intervalKey = candles.length >= 2 ? candles[1].ts - candles[0].ts : 0;
  useEffect(() => {
    if (chartRef.current && candles.length >= 2) {
      chartRef.current.applyOptions({
        timeScale: { tickMarkFormatter: makeTickFormatter(candles) },
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalKey]);

  const prevCandlesRef = useRef<Candle[]>([]);

  useEffect(() => {
    if (!candles.length || !candleRef.current || !volRef.current || !vwapRef.current) return;
    const t = (ts: number) => Math.floor(ts / 1000) as UTCTimestamp;

    candleRef.current.setData(candles.map(c => ({
      time: t(c.ts), open: c.open, high: c.high, low: c.low, close: c.close,
    })));
    volRef.current.setData(candles.map(c => ({
      time: t(c.ts), value: c.volume,
      color: c.close >= c.open ? C.volUp : C.volDn,
    })));
    const vw = calcVWAP(candles);
    vwapRef.current.setData(candles.map((c, i) => ({ time: t(c.ts), value: vw[i] })));

    const prev = prevCandlesRef.current;
    const isSymbolSwap = !prev.length || prev[0].ts !== candles[0].ts || prev.length !== candles.length;
    prevCandlesRef.current = candles;

    if (isSymbolSwap) {
      if (candles.length > 80) {
        chartRef.current?.timeScale().setVisibleLogicalRange({ from: candles.length - 80, to: candles.length + 3 });
      } else {
        chartRef.current?.timeScale().fitContent();
      }
    } else {
      chartRef.current?.timeScale().scrollToRealTime();
    }
  }, [candles]);

  useEffect(() => {
    if (!chartRef.current || !candles.length) return;
    anchorRefs.current.forEach(s => { try { chartRef.current!.removeSeries(s); } catch {} });
    anchorRefs.current = [];
    anchorPoints.forEach((ats, i) => {
      const idx   = findNearestCandle(candles, ats);
      const av    = calcAnchoredVWAP(candles, idx);
      const color = C.anchors[i % C.anchors.length];
      const s = (chartRef.current as any).addLineSeries({
        color, lineWidth: 1, lineStyle: LineStyle.Dashed,
        priceScaleId: 'right', crosshairMarkerVisible: false,
        lastValueVisible: true, title: `AVWAP ${i + 1}`,
      });
      s.setData(candles.map((c, k) => ({
        time: Math.floor(c.ts / 1000) as UTCTimestamp, value: av[k],
      })).filter((d: any) => !isNaN(d.value)));
      anchorRefs.current.push(s);
    });
  }, [anchorPoints, candles]);

  useEffect(() => {
    if (!candleRef.current || !candles.length) return;
    plinesRef.current.forEach(pl => { try { candleRef.current!.removePriceLine(pl); } catch {} });
    plinesRef.current = [];
    zones.filter(z => z.sig === 'High').slice(0, 10).forEach(zone => {
      const color = zone.type === 'resistance'
        ? 'rgba(239,83,80,.6)'
        : zone.type === 'support'
          ? 'rgba(38,166,154,.6)'
          : 'rgba(201,168,76,.6)';
      plinesRef.current.push(candleRef.current!.createPriceLine({
        price: zone.price, color, lineWidth: 1,
        lineStyle: LineStyle.Dotted, axisLabelVisible: true,
        title: zone.label.substring(0, 20),
      }));
    });
  }, [zones, candles]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    if (!anchorMode || !chartRef.current || !candles.length) return;
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const ct = chartRef.current.timeScale().coordinateToTime(e.clientX - rect.left);
    if (ct) {
      const nearest = findNearestCandle(candles, (ct as number) * 1000);
      onAddAnchor(candles[nearest].ts);
      setAnchorMode(false);
    }
  }, [anchorMode, candles, onAddAnchor]);

  // ── Empty state ─────────────────────────────────────────────────────────────
  if (!candles.length) return (
    <div style={{
      height: CHART_H, display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center',
      background: C.bg, borderRadius: 6,
      border: `1px solid ${C.axisLine}`,
    }}>
      <div style={{ fontSize: 26, opacity: .15, marginBottom: 8 }}>📊</div>
      <div style={{ fontSize: 11, color: C.text, fontWeight: 600, fontFamily: 'var(--mono)' }}>No OHLCV Data</div>
      <div style={{ fontSize: 10, color: '#454d5a', marginTop: 3, fontFamily: 'var(--mono)' }}>Fetch via API or import CSV</div>
    </div>
  );

  // ── Chart canvas + minimal controls ─────────────────────────────────────────
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {/* Chart canvas — primary visual surface */}
      <div
        ref={containerRef}
        style={{
          width: '100%', borderRadius: 6, overflow: 'hidden',
          cursor: anchorMode ? 'crosshair' : 'default',
          border: 'none',           /* no border — chart IS the surface */
        }}
        onClick={handleClick}
      />

      {/* Controls strip — nearly invisible ──────────────────────────── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 5,
        padding: '2px 2px 0', flexWrap: 'wrap',
        background: 'transparent',
        opacity: 0.65,                /* visible but refined — not fading */
      }}>
        {/* AVWAP toggle */}
        <button
          onClick={() => setAnchorMode(m => !m)}
          style={{
            padding: '2px 7px', borderRadius: 4, fontSize: 9, fontWeight: 600,
            fontFamily: 'var(--mono)', cursor: 'pointer',
            border: `1px solid ${anchorMode ? 'rgba(34,211,238,.45)' : 'rgba(255,255,255,.16)'}`,
            background: anchorMode ? 'rgba(34,211,238,.10)' : 'transparent',
            color: anchorMode ? '#22d3ee' : '#252d3a',
            lineHeight: '16px',
          }}
        >
          {anchorMode ? '◉ click chart' : '⊕ AVWAP'}
        </button>

        {/* Active AVWAP chips */}
        {anchorPoints.map((ts, i) => (
          <div key={ts} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <div style={{ width: 6, height: 6, borderRadius: 1, background: C.anchors[i % 4], flexShrink: 0 }} />
<span style={{ fontSize: 8, color: '#2d3444', fontFamily: 'var(--mono)' }}>A{i + 1}</span>
            <button
              onClick={() => onRemoveAnchor(ts)}
              style={{ fontSize: 8, color: '#6b3030', background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 1 }}
            >✕</button>
          </div>
        ))}

        {/* Legend — minimal, right-aligned */}
<div style={{ marginLeft: 'auto', display: 'flex', gap: 8, fontSize: 8, color: '#1f2636', fontFamily: 'var(--mono)' }}>
          <span style={{ color: C.bullBody }}>▲</span>
          <span style={{ color: C.bearBody }}>▼</span>
          <span style={{ color: C.vwap }}>— V</span>
        </div>
      </div>
    </div>
  );
}

// ─── SMC Overlay ─────────────────────────────────────────────────────────────

interface SMCOverlayProps {
  smc: SMCResult;
  overlays: SMCOverlaySettings;
  candles: Candle[];
  chartWidth: number;
  chartHeight: number;
}

export function SMCOverlay({ smc, overlays, candles, chartWidth, chartHeight }: SMCOverlayProps) {
  if (!candles.length || !chartWidth) return null;

  const priceMin   = Math.min(...candles.slice(-80).map(c => c.low));
  const priceMax   = Math.max(...candles.slice(-80).map(c => c.high));
  const priceRange = priceMax - priceMin;
  const padded     = priceRange * 0.05;
  const pLow       = priceMin - padded;
  const pHigh      = priceMax + padded;

  const PAD_L = 0, PAD_R = 52, PAD_T = 10, PAD_B = 65;
  const chartW  = chartWidth  - PAD_L - PAD_R;
  const usableH = chartHeight - PAD_T - PAD_B;
  const pY = (price: number) => PAD_T + usableH - ((price - pLow) / (pHigh - pLow)) * usableH;

  const renderZoneRect = (top: number, bottom: number, color: string, alpha: number, label: string, key: string) => {
    const y1 = pY(top), y2 = pY(bottom);
    if (y1 > chartHeight || y2 < 0) return null;
    const h = Math.max(2, Math.abs(y2 - y1));
    return (
      <g key={key}>
        <rect x={PAD_L} y={Math.min(y1, y2)} width={chartW} height={h} fill={color} fillOpacity={alpha} />
        <text x={chartW - 2} y={Math.min(y1, y2) + 9} textAnchor="end"
          fill={color} fillOpacity={0.85} fontSize={8} fontFamily="JetBrains Mono">{label}</text>
      </g>
    );
  };

  const renderHLine = (price: number, color: string, label: string, key: string, dashed = false) => {
    const y = pY(price);
    if (y < 0 || y > chartHeight) return null;
    return (
      <g key={key}>
        <line x1={PAD_L} x2={chartW} y1={y} y2={y}
          stroke={color} strokeWidth={1} strokeOpacity={0.7}
          strokeDasharray={dashed ? '4,3' : 'none'} />
        <text x={chartW - 2} y={y - 2} textAnchor="end"
          fill={color} fillOpacity={0.85} fontSize={8} fontFamily="JetBrains Mono">{label}</text>
      </g>
    );
  };

  return (
    <svg style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', width: '100%', height: '100%' }}
      viewBox={`0 0 ${chartWidth} ${chartHeight}`} preserveAspectRatio="none">
      {overlays.showFVGs && smc.allFVGs.slice(0, 3).map(fvg =>
        renderZoneRect(fvg.top, fvg.bottom, fvg.dir === 'bullish' ? '#26a69a' : '#ef5350', 0.09,
          `FVG ${fvg.significance === 'major' ? '★' : ''}`, fvg.id))}
      {overlays.showOrderBlocks && smc.allOBs.slice(0, 2).map(ob =>
        renderZoneRect(ob.top, ob.bottom, ob.type === 'bullish' ? '#26a69a' : '#ef5350', 0.13,
          `OB ${ob.strength === 'major' ? '★' : ''}`, ob.id))}
      {overlays.showPremiumDiscount && smc.premiumDiscount &&
        renderHLine(smc.premiumDiscount.equilibrium, C.vwap, 'EQ', 'pd-eq', true)}
      {overlays.showSweeps && smc.recentSweep &&
        renderHLine(smc.recentSweep.level, '#22d3ee', `Sweep${smc.recentSweep.reclaimed ? ' ✓' : ''}`, 'sweep', true)}
      {overlays.showBOSCHOCH && smc.structureEvents.slice(0, 2).map((e, i) => {
        const y = pY(e.price);
        if (y < 0 || y > chartHeight) return null;
        const col = e.type === 'CHOCH' ? '#f472b6' : e.dir === 'bullish' ? '#26a69a' : '#ef5350';
        return (
          <g key={`struct-${i}`}>
            <line x1={PAD_L + 8} x2={chartW - 8} y1={y} y2={y}
              stroke={col} strokeWidth={1} strokeOpacity={0.55} strokeDasharray="3,2" />
            <rect x={PAD_L + 6} y={y - 8} width={e.type === 'CHOCH' ? 38 : 28} height={14} rx={3}
              fill={col} fillOpacity={0.18} />
            <text x={PAD_L + 10} y={y + 3} fill={col} fillOpacity={0.9}
              fontSize={8} fontWeight="600" fontFamily="JetBrains Mono">{e.type}</text>
          </g>
        );
      })}
    </svg>
  );
}
